// Import model
import { RoomSelection } from '../models/RoomSelection';

// Import base class serializer

// Import dependency types (models and enums used in properties)
import { LineItem } from '../models/LineItem';
import { OtgMeasurement } from '../models/OtgMeasurement';

// Import dependency serializers (for complex types)
import { LineItemSerializer } from './LineItemSerializer';
import { OtgMeasurementSerializer } from './OtgMeasurementSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for RoomSelection - handles JSON serialization and deserialization with type validation
 */
export class RoomSelectionSerializer {
  /**
   * Deserializes a JSON string into a RoomSelection instance
   */
  static deserialize(json: string): RoomSelection {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a RoomSelection instance
   */
  static fromObject(obj: any): RoomSelection {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to RoomSelection');
    }


    const instance: RoomSelection = {
      installationTypeId: obj['installationTypeId'] as string | undefined,
      installationTypeIdName: obj['installationTypeId|Name'] as string | undefined,
      productType: obj['productType'] as string | undefined,
      productTypeName: obj['productType|Name'] as string | undefined,
      patternTypeId: obj['patternTypeId'] as string | undefined,
      patternTypeIdName: obj['patternTypeId|Name'] as string | undefined,
      trimWorkId: obj['trimWorkId'] as string | undefined,
      trimWorkIdName: obj['trimWorkId|Name'] as string | undefined,
      underlaymentId: obj['underlaymentId'] as string | undefined,
      underlaymentIdName: obj['underlaymentId|Name'] as string | undefined,
      catalogFeedId: obj['catalogFeedId'] as string | undefined,
      catalogFeedIdName: obj['catalogFeedId|Name'] as string | undefined,
      itemId: obj['itemId'] as string | undefined,
      itemIdName: obj['itemId|Name'] as string | undefined,
      styleNumber: obj['styleNumber'] as string | undefined,
      bin: obj['bin'] as string | undefined,
      projectExternalId: obj['projectExternalId'] as string | undefined,
      projectExternalIdName: obj['projectExternalId|Name'] as string | undefined,
      roomIds: obj['roomIds'] ? (obj['roomIds'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      createdByUserId: obj['createdByUserId'] as string | undefined,
      createdByUserIdName: obj['createdByUserId|Name'] as string | undefined,
      area: obj['area'] ? OtgMeasurementSerializer.fromObject(obj['area']) : undefined as any,
      mainProductQuantity: obj['mainProductQuantity'] ? OtgMeasurementSerializer.fromObject(obj['mainProductQuantity']) : undefined as any,
      perimeter: obj['perimeter'] ? OtgMeasurementSerializer.fromObject(obj['perimeter']) : undefined as any,
      wasteFactor: obj['wasteFactor'] as number | undefined,
      lineItems: obj['lineItems'] ? (obj['lineItems'] as any[]).map((item: any) =>
        LineItemSerializer.fromObject(item)
      ) : [] as any,
      binId: obj['binId'] as string | undefined,
      sessionKey: obj['sessionKey'] as string | undefined,
      hash: obj['hash'] as string | undefined,
      hasWarnings: obj['hasWarnings'] as boolean | undefined,
      totalCost: obj['totalCost'] as number | undefined,
      totalPrice: obj['totalPrice'] as number | undefined,
      totalTax: obj['totalTax'] as number | undefined,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      isActive: obj['isActive'] as boolean | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of RoomSelection instances
   */
  static deserializeArray(json: string): RoomSelection[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a RoomSelection instance to a JSON string
   */
  static serialize(instance: RoomSelection): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a RoomSelection instance to a plain object
   */
  static toObject(instance: RoomSelection): any {
    return {
      'installationTypeId': instance.installationTypeId,
      'installationTypeId|Name': instance.installationTypeIdName,
      'productType': instance.productType,
      'productType|Name': instance.productTypeName,
      'patternTypeId': instance.patternTypeId,
      'patternTypeId|Name': instance.patternTypeIdName,
      'trimWorkId': instance.trimWorkId,
      'trimWorkId|Name': instance.trimWorkIdName,
      'underlaymentId': instance.underlaymentId,
      'underlaymentId|Name': instance.underlaymentIdName,
      'catalogFeedId': instance.catalogFeedId,
      'catalogFeedId|Name': instance.catalogFeedIdName,
      'itemId': instance.itemId,
      'itemId|Name': instance.itemIdName,
      'styleNumber': instance.styleNumber,
      'bin': instance.bin,
      'projectExternalId': instance.projectExternalId,
      'projectExternalId|Name': instance.projectExternalIdName,
      'roomIds': instance.roomIds?.map((item: any) =>
        item
      ),
      'name': instance.name,
      'description': instance.description,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'createdByUserId': instance.createdByUserId,
      'createdByUserId|Name': instance.createdByUserIdName,
      'area': instance.area ? OtgMeasurementSerializer.toObject(instance.area) : undefined,
      'mainProductQuantity': instance.mainProductQuantity ? OtgMeasurementSerializer.toObject(instance.mainProductQuantity) : undefined,
      'perimeter': instance.perimeter ? OtgMeasurementSerializer.toObject(instance.perimeter) : undefined,
      'wasteFactor': instance.wasteFactor,
      'lineItems': instance.lineItems?.map((item: any) =>
        LineItemSerializer.toObject(item)
      ),
      'binId': instance.binId,
      'sessionKey': instance.sessionKey,
      'hash': instance.hash,
      'hasWarnings': instance.hasWarnings,
      'totalCost': instance.totalCost,
      'totalPrice': instance.totalPrice,
      'totalTax': instance.totalTax,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'isActive': instance.isActive,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
    };
  }

  /**
   * Serializes an array of RoomSelection instances to a JSON string
   */
  static serializeArray(instances: RoomSelection[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
